import React, { useMemo } from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { FileArtifactProto } from "build-ts/dataframe";
import { FileType } from "build-ts/common";
import { AppIcons } from "constants/icons";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import { DownloadMenu } from "modules/common/DownloadMenu";
import {
  copyRawTableToClipboard,
  downloadRawTableAsExcel,
  extractFormattedDataFromPandasDf,
  getPandasDfRowColCount,
} from "modules/artifacts/dataframe/utils/pandasTableUtils";
import PandasTable from "modules/artifacts/dataframe/components/PandasTable";

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
};

export interface FileArtifactProps {
  data: FileArtifactProto;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
}

/**
 * Convert a Uint8Array to a base64 string.
 * Processes in 8 KB chunks to avoid call-stack overflow on large images.
 */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = Array.from(bytes.subarray(i, i + chunkSize));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/**
 * Pure UI component for FILE_ARTIFACT artifacts.
 * Routes to a type-specific content view based on file_type.
 */
const FileArtifact: React.FC<FileArtifactProps> = ({ data, isExpanded, onExpandToggle }) => {
  const displayName = data.header?.name ?? "Uploaded File";
  const pandasDf = data.pandas_dataframe ?? null;
  const isCsv = data.file_type === FileType.CSV && !!pandasDf;
  const isImage = (data.file_type === FileType.PNG || data.file_type === FileType.JPEG) && !!data.image_bytes?.length;
  const isPdf = data.file_type === FileType.PDF && !!data.pdf_bytes?.length;

  const imageMime = data.file_type === FileType.PNG ? "image/png" : "image/jpeg";

  const { rows: numberOfRows, columns: numberOfColumns } = useMemo(
    () => getPandasDfRowColCount(pandasDf ?? undefined),
    [pandasDf]
  );

  // Build the base64 data URL for images once — memoized so we don't re-encode on every render.
  const imageSrc = useMemo(() => {
    if (!isImage || !data.image_bytes?.length) return null;
    return `data:${imageMime};base64,${uint8ToBase64(data.image_bytes)}`;
  }, [isImage, data.image_bytes, imageMime]);

  // Build the base64 data URL for PDF iframe rendering.
  const pdfSrc = useMemo(() => {
    if (!isPdf || !data.pdf_bytes?.length) return null;
    return `data:application/pdf;base64,${uint8ToBase64(data.pdf_bytes)}`;
  }, [isPdf, data.pdf_bytes]);

  const headerActions = (
    <>
      {isCsv && numberOfRows > 0 && numberOfColumns > 0 && (
        <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
          {numberOfColumns} {numberOfColumns > 1 ? "cols" : "col"}, {numberOfRows} {numberOfRows > 1 ? "rows" : "row"}
        </Text>
      )}

      {isCsv && pandasDf?.data_json && (
        <DownloadMenu
          ariaLabel="Download"
          items={[
            {
              label: "Copy to clipboard",
              icon: "copy",
              onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(pandasDf)),
            },
            {
              label: "Download Excel file",
              icon: "fileExcel",
              onClick: () =>
                downloadRawTableAsExcel(
                  extractFormattedDataFromPandasDf(pandasDf),
                  `${pandasDf.name ?? displayName}.xlsx`
                ),
            },
          ]}
        />
      )}
    </>
  );

  const tablePillName = pandasDf?.name ?? displayName;

  const renderCsvTable = () => {
    if (!pandasDf?.data_json) {
      return (
        <Box px={4} py={6} textAlign="center">
          <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
            Nothing to show. Please try again...
          </Text>
        </Box>
      );
    }

    return (
      <>
        <HStack
          spacing={1.5}
          px={2}
          py={1.5}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          flexWrap="wrap"
          position="sticky"
          top={0}
          zIndex={2}
          boxShadow="sm"
        >
          <Box
            display="inline-flex"
            alignItems="center"
            px={3}
            py={1}
            borderRadius="md"
            border="2px solid"
            borderColor="purple.600"
            bg="white"
            color="purple.700"
            fontSize="xs"
            fontWeight="700"
          >
            <HStack as="span" spacing={1.5} align="center">
              <Icon as={AppIcons.dataframe} boxSize={3} color="currentColor" flexShrink={0} aria-hidden />
              <Text as="span" fontSize="xs" lineHeight="1.2" mb={0} isTruncated>
                {tablePillName}
              </Text>
            </HStack>
          </Box>
        </HStack>
        <PandasTable df={pandasDf} />
      </>
    );
  };

  const renderImage = () => {
    if (!imageSrc) {
      return (
        <Box px={4} py={6} textAlign="center">
          <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
            Image could not be loaded.
          </Text>
        </Box>
      );
    }
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="flex-start">
        <Box
          as="img"
          src={imageSrc}
          alt={displayName}
          maxW="100%"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        />
      </Box>
    );
  };

  const renderPdf = () => {
    if (!pdfSrc) {
      return (
        <Box px={4} py={6} textAlign="center">
          <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
            PDF could not be loaded.
          </Text>
        </Box>
      );
    }
    return <Box as="iframe" src={pdfSrc} width="100%" flex="1 1 0" minH={0} border="none" title={displayName} />;
  };

  const renderContent = () => {
    if (isCsv) return renderCsvTable();
    if (isImage) return renderImage();
    if (isPdf) return renderPdf();

    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          Preview is not available for this file type yet.
        </Text>
      </Box>
    );
  };

  const typeLabel = isImage ? "Image" : isPdf ? "Document" : "File";

  return (
    <Box position="relative" display="flex" flexDirection="column" width="100%" flex="1 1 0" minH={0} overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.file}
        iconBg="white"
        iconColor="gray.600"
        name={displayName}
        typeLabel={typeLabel}
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY={isPdf ? "hidden" : "auto"}
        overflowX="hidden"
        onWheel={e => e.stopPropagation()}
        sx={isPdf ? undefined : SCROLL_SX}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default FileArtifact;
